import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { Sparkles, Calendar, Camera, Users, Lock, Radio } from "lucide-react";

/**
 * Public "Eventos ao vivo" — social proof list of currently-active events.
 * Does NOT expose gallery URLs. Guests still need the QR code to access.
 */
export default function VipShowcasePage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/live-events")
      .then((r) => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    const int = setInterval(() => {
      api.get("/public/live-events").then((r) => setEvents(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(int);
  }, []);

  const vipCount = events.filter((e) => e.is_vip).length;
  const totalPhotos = events.reduce((s, e) => s + (e.photos_count || 0), 0);

  return (
    <div className="min-h-screen text-white grain">
      <Navbar />

      <section className="relative py-16 lg:py-24 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FF007F]/15 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-[#00F0FF]/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 mb-6 text-xs uppercase tracking-[0.2em]">
              <span className="w-2 h-2 rounded-full bg-[#39FF14] pulse-ring" />
              Ao vivo agora
            </div>
            <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tighter">
              Eventos <span className="text-[#FF007F] text-glow-pink">ao vivo</span>
            </h1>
            <p className="text-white/60 mt-4 text-lg leading-relaxed">
              Festas rolando neste momento na Festagram. As galerias são
              privadas — só quem tem o QR code do evento consegue acessar.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
            {[
              { l: "Ao vivo", v: events.length, c: "#39FF14" },
              { l: "Fotos", v: totalPhotos, c: "#00F0FF" },
              { l: "VIPs", v: vipCount, c: "#FF007F" },
            ].map((k, i) => (
              <div key={i} className="glass rounded-xl p-4 text-center" data-testid={`live-stat-${k.l}`}>
                <div className="font-display font-black text-3xl" style={{ color: k.c }}>
                  {k.v}
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 mt-1">
                  {k.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-12 text-white/50">Carregando...</div>
          ) : events.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center max-w-lg mx-auto">
              <Radio className="w-10 h-10 text-white/20 mx-auto mb-4" />
              <h3 className="font-display font-bold text-xl tracking-tight">
                Nenhum evento ao vivo agora
              </h3>
              <p className="text-white/50 mt-2">
                Quando alguém estiver rolando, aparece por aqui.
              </p>
              <Link to="/register" className="btn-neon mt-6 inline-flex">
                Criar meu evento
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="live-grid">
              {events.map((ev, i) => (
                <motion.div
                  key={ev.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="glass rounded-2xl p-6 relative overflow-hidden"
                  data-testid={`live-event-${ev.id}`}
                >
                  {ev.is_vip && (
                    <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#FF007F]/25 blur-2xl" />
                  )}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                      <div className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] pulse-ring" />
                        ao vivo
                      </div>
                      {ev.is_vip && (
                        <span className="badge-vip">
                          <Sparkles className="w-3 h-3" /> VIP
                        </span>
                      )}
                    </div>
                    <h3 className="font-display font-bold text-xl tracking-tight">
                      {ev.name}
                    </h3>
                    <p className="text-xs text-white/50 mt-1">
                      por {ev.owner_name || "Anfitrião"}
                    </p>

                    <div className="flex items-center gap-4 mt-5 pt-4 border-t border-white/5 text-xs text-white/60">
                      <span className="flex items-center gap-1.5">
                        <Camera className="w-3.5 h-3.5 text-[#00F0FF]" />
                        <span className="text-white font-bold">
                          {ev.photos_count}
                        </span>{" "}
                        fotos
                      </span>
                      {ev.date && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(ev.date).toLocaleDateString("pt-BR")}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-[11px] text-white/40">
                      <Lock className="w-3 h-3" />
                      Galeria privada — acesso apenas via QR code
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
